import React, { useState } from 'react';
import { Home, MapPin, Users, DollarSign, Star, TrendingUp, Calendar, Search, Download, Plus, Eye, Filter, Edit, Trash2, Save } from 'lucide-react';

const VillaDetail = ({ villa, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [activePricingTab, setActivePricingTab] = useState('basic');
  
  // État pour les règles d'annulation personnalisables
  const [cancellationRules, setCancellationRules] = useState([
    { id: 1, daysFrom: 90, daysTo: null, percentage: 0, label: 'Plus de 90 jours', color: 'green', desc: 'Remboursement complet' },
    { id: 2, daysFrom: 60, daysTo: 90, percentage: 25, label: '60 à 90 jours', color: 'yellow', desc: '25% de pénalité' },
    { id: 3, daysFrom: 30, daysTo: 60, percentage: 50, label: '30 à 60 jours', color: 'orange', desc: '50% de pénalité' },
    { id: 4, daysFrom: 7, daysTo: 30, percentage: 75, label: '7 à 30 jours', color: 'red', desc: '75% de pénalité' },
    { id: 5, daysFrom: 0, daysTo: 7, percentage: 100, label: 'Moins de 7 jours', color: 'red', desc: 'Non remboursable' }
  ]);
  const [editingRuleId, setEditingRuleId] = useState(null);

  // Helper functions for styling
  const getCategoryColor = (category) => {
    const colors = {
      'Luxury': 'bg-purple-100 text-purple-800',
      'Ultra Luxury': 'bg-yellow-100 text-yellow-800',
      'Premium': 'bg-blue-100 text-blue-800',
      'Standard': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getSourceColor = (source) => {
    const colors = {
      'Direct': 'bg-green-100 text-green-800',
      'Airbnb': 'bg-red-100 text-red-800',
      'VRBO': 'bg-blue-100 text-blue-800',
      'Booking.com': 'bg-purple-100 text-purple-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  // Valeurs par défaut pour éviter les erreurs
  const villaData = {
    ...villa,
    gpsCoordinates: villa.gpsCoordinates || '17.8962°N, 62.8498°W',
    gateCode: villa.gateCode || 'CODE123',
    wifiCode: villa.wifiCode || 'WiFiPassword123',
    parkingCode: villa.parkingCode || '1234',
    safeCode: villa.safeCode || '9876',
    staff: villa.staff || [
      {
        name: 'Manager Villa',
        role: 'Villa Manager',
        phone: '+590 690 12 34 56',
        email: 'manager@priviastaff.com',
        languages: ['Français', 'Anglais'],
        emergency: true
      }
    ],
    usageNotes: villa.usageNotes || {
      pool: 'Piscine disponible 24h/24',
      security: 'Système d\'alarme activé automatiquement',
      aircon: 'Climatisation centralisée',
      wifi: 'WiFi haut débit disponible partout'
    },
    services: villa.services || {
      housekeeping: 'Service quotidien',
      gardening: 'Entretien régulier',
      poolMaintenance: 'Maintenance hebdomadaire',
      security: 'Surveillance 24/7'
    }
  };

  // Fonctions pour gérer les règles d'annulation
  const generateRuleDisplayText = (rule) => {
    if (rule.daysTo === null) {
      return `${rule.daysFrom}+ jours`;
    } else if (rule.daysFrom === 0) {
      return `<${rule.daysTo} jours`;
    } else {
      return `${rule.daysFrom}-${rule.daysTo} jours`;
    }
  };

  const addNewRule = () => {
    const newId = Math.max(...cancellationRules.map(r => r.id)) + 1;
    const newRule = {
      id: newId,
      daysFrom: 14,
      daysTo: 21,
      percentage: 50,
      label: 'Nouvelle période',
      color: 'blue',
      desc: 'Nouvelle règle'
    };
    setCancellationRules([...cancellationRules, newRule]);
    setEditingRuleId(newId);
  };

  const updateRule = (id, field, value) => {
    setCancellationRules(cancellationRules.map(rule => 
      rule.id === id ? { ...rule, [field]: value } : rule
    ));
  };

  const deleteRule = (id) => {
    setCancellationRules(cancellationRules.filter(rule => rule.id !== id));
  };

  const saveRule = (id) => {
    setEditingRuleId(null);
  };

  return (
    <div className="bg-white">
      {/* Header avec retour */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800"
            >
              ← Retour aux villas
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{villaData.name}</h1>
              <p className="text-gray-600">{villaData.destination}, {villaData.location}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getCategoryColor(villaData.category)}`}>
              {villaData.category}
            </span>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getSourceColor(villaData.source)}`}>
              {villaData.source}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'overview', label: 'Vue d\'ensemble' },
            { id: 'specs', label: 'Caractéristiques' },
            { id: 'pricing', label: 'Tarification Avancée' },
            { id: 'cancellation', label: 'Politique d\'Annulation' },
            { id: 'bookings', label: 'Réservations' },
            { id: 'photos', label: 'Photos' },
            { id: 'staff', label: 'Staff & Services' },
            { id: 'maintenance', label: 'Maintenance' },
            { id: 'analytics', label: 'Analytics' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-800">€{(villaData.pricePerWeek / 1000).toFixed(0)}K</div>
                <div className="text-sm text-gray-600">Prix/Semaine</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-800">{villaData.occupancy}%</div>
                <div className="text-sm text-gray-600">Occupation</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-800">{villaData.rating}</div>
                <div className="text-sm text-gray-600">Note Moyenne</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-800">€{(villaData.revenueYTD / 1000000).toFixed(1)}M</div>
                <div className="text-sm text-gray-600">Revenue YTD</div>
              </div>
            </div>

            {/* Informations pratiques */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Informations Pratiques</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Coordonnées GPS:</span>
                    <span className="font-medium">{villaData.gpsCoordinates}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Code Portail:</span>
                    <span className="font-medium">{villaData.gateCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Code WiFi:</span>
                    <span className="font-medium">{villaData.wifiCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Code Parking:</span>
                    <span className="font-medium">{villaData.parkingCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Code Coffre:</span>
                    <span className="font-medium">{villaData.safeCode}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Notes d'Usage</h3>
                <div className="space-y-3">
                  {Object.entries(villaData.usageNotes).map(([key, note]) => (
                    <div key={key} className="border-l-4 border-yellow-500 pl-3">
                      <div className="font-semibold text-gray-800 capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                      <div className="text-sm text-gray-600">{note}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Placeholder for other tabs */}
        {activeTab === 'specs' && (
          <div className="space-y-6">
            {/* Caractéristiques générales */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Caractéristiques de la villa</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Espaces</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Chambres:</span>
                      <span className="font-medium">{villaData.bedrooms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Salles de bain:</span>
                      <span className="font-medium">{villaData.bathrooms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Capacité max:</span>
                      <span className="font-medium">{villaData.maxGuests} personnes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surface:</span>
                      <span className="font-medium">450 m²</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Équipements</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Piscine privée chauffée</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>WiFi haut débit</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Climatisation centralisée</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Système audio Sonos</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-500">✓</span>
                      <span>Coffre-fort électronique</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Services inclus</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-500">•</span>
                      <span>Service de ménage quotidien</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-500">•</span>
                      <span>Concierge 24/7</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-500">•</span>
                      <span>Chef sur demande</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-500">•</span>
                      <span>Maintenance piscine</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-500">•</span>
                      <span>Sécurité 24/7</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="space-y-6">
            {/* Tarification Avancée - Version avec saisons sélectionnables */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">TARIFICATION AVANCÉE</h3>
                    <p className="text-gray-600">Configuration des tarifs pour {villaData.name}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                      Analyser Performance
                    </button>
                    <button className="px-4 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                      Optimisation IA
                    </button>
                    <button className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                      Sauvegarder Tout
                    </button>
                  </div>
                </div>

                {/* Statistiques Rapides (Quick Stats) */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-800">€45K</div>
                    <div className="text-xs text-gray-600">ADR Actuel</div>
                    <div className="text-xs text-green-600">+15%</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-800">87%</div>
                    <div className="text-xs text-gray-600">Occupation</div>
                    <div className="text-xs text-green-600">+8%</div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-800">€38.5K</div>
                    <div className="text-xs text-gray-600">RevPAN</div>
                    <div className="text-xs text-green-600">+23%</div>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-800">5</div>
                    <div className="text-xs text-gray-600">Saisons</div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-800">8/8</div>
                    <div className="text-xs text-gray-600">Chambres</div>
                  </div>
                  <div className="bg-teal-50 p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-800">14</div>
                    <div className="text-xs text-gray-600">Séjour Moyen</div>
                  </div>
                </div>
              </div>

              {/* Layout principal */}
              <div className="flex">
                {/* Gestionnaire de Saisons */}
                <div className="w-80 border-r border-gray-200 bg-gray-50">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-semibold text-gray-800">Gestionnaire de Saisons</h4>
                      <button className="text-sm text-blue-600 hover:text-blue-800">+ Nouvelle</button>
                    </div>
                    
                    {/* Saisons configurées - sélectionnables */}
                    <div className="space-y-3 mb-6">
                      {[
                        { id: 'ultra-peak', name: 'Ultra Peak', dates: '15 Déc - 15 Jan', color: 'red', priceNight: '€65,000', priceWeek: '€420,000' },
                        { id: 'peak', name: 'Peak Season', dates: '16 Nov - 14 Déc', color: 'orange', priceNight: '€48,000', priceWeek: '€310,000' },
                        { id: 'high', name: 'High Season', dates: '16 Jun - 15 Sep', color: 'yellow', priceNight: '€35,000', priceWeek: '€245,000' },
                        { id: 'mid', name: 'Mid Season', dates: '16 Apr - 15 Jun', color: 'green', priceNight: '€28,000', priceWeek: '€196,000' },
                        { id: 'low', name: 'Low Season', dates: '16 Sep - 15 Nov', color: 'blue', priceNight: '€22,000', priceWeek: '€154,000' }
                      ].map((season, index) => (
                        <div 
                          key={index} 
                          className={`bg-white border border-gray-200 rounded-lg p-3 cursor-pointer transition-colors ${
                            activePricingTab === season.id ? 'border-yellow-500 bg-yellow-50' : 'hover:border-gray-300'
                          }`}
                          onClick={() => setActivePricingTab(season.id)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full bg-${season.color}-500`}></div>
                              <span className="font-medium text-gray-800 text-sm">{season.name}</span>
                            </div>
                            <button 
                              className="text-gray-400 hover:text-blue-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivePricingTab(season.id);
                              }}
                            >
                              ✏️
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 mb-2">{season.dates}</div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Nuit:</span>
                              <div className="font-semibold">{season.priceNight}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Semaine:</span>
                              <div className="font-semibold">{season.priceWeek}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Dates Spéciales */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-gray-800">Dates Spéciales</h4>
                        <button className="text-sm text-blue-600 hover:text-blue-800">+ Ajouter</button>
                      </div>
                      <div className="space-y-2">
                        {[
                          { name: 'Nouvel An', dates: '31 Déc - 2 Jan', adjustment: '+40%' },
                          { name: 'Carnaval', dates: '15-20 Fév', adjustment: '+25%' }
                        ].map((special, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded p-2">
                            <div className="text-xs font-medium text-gray-800">{special.name}</div>
                            <div className="text-xs text-gray-500">{special.dates}</div>
                            <div className="text-xs text-green-600 font-medium">{special.adjustment}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Configuration des Tarifs pour la saison sélectionnée */}
                <div className="flex-1">
                  <div className="p-6">
                    {/* Configuration de la saison sélectionnée */}
                    {activePricingTab === 'ultra-peak' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-red-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Ultra Peak Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>

                        {/* Tarifs de base */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-gray-800 mb-4">Tarifs Standards</h5>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Prix par nuit</label>
                                <div className="relative">
                                  <input type="number" defaultValue="65000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" />
                                  <span className="absolute right-3 top-2 text-sm text-gray-500">€</span>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Prix par semaine</label>
                                <div className="relative">
                                  <input type="number" defaultValue="420000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" />
                                  <span className="absolute right-3 top-2 text-sm text-gray-500">€</span>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Prix 14 nuits</label>
                                <div className="relative">
                                  <input type="number" defaultValue="780000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" />
                                  <span className="absolute right-3 top-2 text-sm text-gray-500">€</span>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Prix mensuel</label>
                                <div className="relative">
                                  <input type="number" defaultValue="1800000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" />
                                  <span className="absolute right-3 top-2 text-sm text-gray-500">€</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-gray-800 mb-4">Variations Weekend</h5>
                            <div className="space-y-3">
                              {[
                                { day: 'Vendredi', value: 10 },
                                { day: 'Samedi', value: 20 },
                                { day: 'Dimanche', value: 5 },
                                { day: 'Jours fériés', value: 40 }
                              ].map((variation, index) => (
                                <div key={index} className="flex items-center justify-between">
                                  <span className="text-sm text-gray-600">{variation.day}:</span>
                                  <div className="flex items-center space-x-2">
                                    <input type="number" defaultValue={variation.value} className="w-16 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-yellow-500" />
                                    <span className="text-sm text-gray-600">%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Tarification par chambres */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-4">Tarification par Nombre de Chambres</h5>
                          <div className="space-y-4">
                            {Array.from({length: villaData.bedrooms}, (_, i) => i + 1).map((chambres) => (
                              <div key={chambres} className="grid grid-cols-4 gap-4 items-center p-3 bg-gray-50 rounded-lg">
                                <div>
                                  <div className="font-medium text-gray-800">{chambres} Chambre{chambres > 1 ? 's' : ''}</div>
                                  <div className="text-xs text-gray-500">
                                    {chambres <= 2 ? '2-4 pers.' : 
                                     chambres <= 4 ? '4-8 pers.' : 
                                     chambres <= 6 ? '8-12 pers.' : '12-16 pers.'}
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">Prix/nuit</label>
                                  <div className="relative">
                                    <input 
                                      type="number" 
                                      defaultValue={Math.round(18000 + (chambres - 1) * 8000)} 
                                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-yellow-500" 
                                    />
                                    <span className="absolute right-2 top-1 text-xs text-gray-500">€</span>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600">Prix/semaine</label>
                                  <div className="relative">
                                    <input 
                                      type="number" 
                                      defaultValue={Math.round((18000 + (chambres - 1) * 8000) * 6.4)} 
                                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-yellow-500" 
                                    />
                                    <span className="absolute right-2 top-1 text-xs text-gray-500">€</span>
                                  </div>
                                </div>
                                <div>
                                  <button className="text-blue-600 hover:text-blue-800 text-sm">Ajuster</button>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Note informative */}
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <div className="text-sm text-blue-800">
                              <strong>Villa {villaData.name}</strong> dispose de {villaData.bedrooms} chambres au total.
                              Les prix s'ajustent automatiquement selon le nombre de chambres utilisées.
                            </div>
                          </div>
                        </div>

                        {/* Réductions long séjour */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-4">Réductions Long Séjour</h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                              { duration: '7+ nuits', discount: 5 },
                              { duration: '14+ nuits', discount: 10 },
                              { duration: '21+ nuits', discount: 15 },
                              { duration: '30+ nuits', discount: 20 }
                            ].map((discount, index) => (
                              <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                                <div className="text-sm text-gray-600 mb-2">{discount.duration}</div>
                                <div className="flex items-center justify-center space-x-1">
                                  <input type="number" defaultValue={discount.discount} className="w-12 px-1 py-1 text-sm border rounded text-center focus:ring-2 focus:ring-yellow-500" />
                                  <span className="text-sm text-blue-600">%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Configuration pour Peak Season */}
                    {activePricingTab === 'peak' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Peak Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration Peak Season</div>
                          <p className="text-sm text-gray-400">Ajustez les tarifs pour la saison Peak (16 Nov - 14 Déc)</p>
                        </div>
                      </div>
                    )}

                    {/* Configuration pour High Season */}
                    {activePricingTab === 'high' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration High Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration High Season</div>
                          <p className="text-sm text-gray-400">Ajustez les tarifs pour la saison High (16 Jun - 15 Sep)</p>
                        </div>
                      </div>
                    )}

                    {/* Configuration pour Mid Season */}
                    {activePricingTab === 'mid' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-green-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Mid Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration Mid Season</div>
                          <p className="text-sm text-gray-400">Ajustez les tarifs pour la saison Mid (16 Apr - 15 Jun)</p>
                        </div>
                      </div>
                    )}

                    {/* Configuration pour Low Season */}
                    {activePricingTab === 'low' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Low Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration Low Season</div>
                          <p className="text-sm text-gray-400">Ajustez les tarifs pour la saison Low (16 Sep - 15 Nov)</p>
                        </div>
                      </div>
                    )}

                    {/* Default - pas de saison sélectionnée */}
                    {!['ultra-peak', 'peak', 'high', 'mid', 'low'].includes(activePricingTab) && (
                      <div className="text-center py-12">
                        <div className="text-gray-500 mb-4">Sélectionnez une saison</div>
                        <p className="text-sm text-gray-400">Cliquez sur une saison dans la sidebar pour configurer ses tarifs</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Panneau d'Optimisation IA */}
                <div className="w-80 border-l border-gray-200 bg-gradient-to-b from-blue-50 to-purple-50">
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-4">🤖 Optimisation IA</h4>
                    
                    <div className="space-y-4">
                      {/* Métriques IA */}
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="text-sm font-medium text-gray-800 mb-2">Demande Actuelle</div>
                        <div className="text-lg font-bold text-green-600">Très Élevée</div>
                        <div className="text-xs text-gray-500">+85% vs normale</div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="text-sm font-medium text-gray-800 mb-2">Prix Suggéré</div>
                        <div className="text-lg font-bold text-blue-600">+12%</div>
                        <div className="text-xs text-gray-500">vs prix de base</div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="text-sm font-medium text-gray-800 mb-2">Compétitivité</div>
                        <div className="text-lg font-bold text-purple-600">Excellent</div>
                        <div className="text-xs text-gray-500">Top 5% de la zone</div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="text-sm font-medium text-gray-800 mb-2">Probabilité Réservation</div>
                        <div className="text-lg font-bold text-orange-600">94%</div>
                        <div className="text-xs text-gray-500">7 prochains jours</div>
                      </div>

                      <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                        Appliquer Suggestions IA
                      </button>

                      <div className="text-xs text-gray-500 mt-2">
                        Revenue estimé: <strong className="text-green-600">+€180K ce mois</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cancellation' && (
          <div className="space-y-6">
            {/* Politique d'Annulation - Version avec saisons sélectionnables */}
            <div className="bg-white border border-gray-200 rounded-lg">
              {/* Header sans sélecteur de villa */}
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">POLITIQUES D'ANNULATION</h3>
                    <p className="text-gray-600">Configuration des règles d'annulation pour {villaData.name}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                      Tester Politique
                    </button>
                    <button className="px-4 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                      Comparer Villas
                    </button>
                    <button className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                      Sauvegarder Tout
                    </button>
                  </div>
                </div>
              </div>

              {/* Layout avec sidebar saisons et contenu */}
              <div className="flex">
                {/* Sidebar avec les saisons sélectionnables */}
                <div className="w-72 border-r border-gray-200 bg-gray-50">
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Saisons configurées</h4>
                    <div className="space-y-2">
                      {[
                        { id: 'ultra-peak-cancel', name: 'Ultra Peak', period: 'Déc 15 - Jan 15', color: 'red' },
                        { id: 'peak-cancel', name: 'Peak Season', period: 'Nov - Avr', color: 'orange' },
                        { id: 'high-cancel', name: 'High Season', period: 'Mai - Juin', color: 'yellow' },
                        { id: 'mid-cancel', name: 'Mid Season', period: 'Jul - Août', color: 'green' },
                        { id: 'low-cancel', name: 'Low Season', period: 'Sep - Oct', color: 'blue' }
                      ].map((season) => (
                        <div 
                          key={season.id} 
                          className={`p-3 bg-white rounded-lg border border-gray-200 cursor-pointer transition-colors ${
                            activePricingTab === season.id ? 'border-yellow-500 bg-yellow-50' : 'hover:border-gray-300'
                          }`}
                          onClick={() => setActivePricingTab(season.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full bg-${season.color}-500`}></div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">{season.name}</div>
                              <div className="text-xs text-gray-500">{season.period}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Templates rapides */}
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Templates rapides</h4>
                      <div className="space-y-2">
                        {[
                          { name: 'Flexible', icon: '😊', desc: 'Remb. complet 7j avant' },
                          { name: 'Modérée', icon: '⚖️', desc: 'Remb. dégressif' },
                          { name: 'Stricte', icon: '🔒', desc: 'Protection maximale' },
                          { name: 'Luxe Premium', icon: '💎', desc: 'Clientèle VIP' }
                        ].map((template, index) => (
                          <div key={index} className="p-2 bg-white rounded border border-gray-200 cursor-pointer hover:border-blue-300">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm">{template.icon}</span>
                              <div>
                                <div className="text-xs font-medium text-gray-800">{template.name}</div>
                                <div className="text-xs text-gray-500">{template.desc}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenu principal - Configuration pour la saison sélectionnée */}
                <div className="flex-1">
                  <div className="p-6">
                    {/* Configuration Ultra Peak Season */}
                    {activePricingTab === 'ultra-peak-cancel' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-red-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Ultra Peak Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>

                        {/* Règles par tranche de temps */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="font-medium text-gray-800">Règles d'annulation par période</h5>
                            <button
                              onClick={addNewRule}
                              className="flex items-center space-x-2 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                            >
                              <Plus size={16} />
                              <span>Ajouter période</span>
                            </button>
                          </div>
                          <div className="space-y-4">
                            {cancellationRules.map((rule) => (
                              <div key={rule.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                                {editingRuleId === rule.id ? (
                                  // Mode édition
                                  <>
                                    <div className="w-32 flex items-center space-x-2">
                                      <input
                                        type="number"
                                        value={rule.daysFrom}
                                        onChange={(e) => updateRule(rule.id, 'daysFrom', parseInt(e.target.value))}
                                        className="w-12 px-2 py-1 text-sm border border-gray-300 rounded"
                                        min="0"
                                      />
                                      <span className="text-xs text-gray-600">à</span>
                                      <input
                                        type="number"
                                        value={rule.daysTo || ''}
                                        onChange={(e) => updateRule(rule.id, 'daysTo', e.target.value ? parseInt(e.target.value) : null)}
                                        className="w-12 px-2 py-1 text-sm border border-gray-300 rounded"
                                        placeholder="∞"
                                      />
                                      <span className="text-xs text-gray-600">j</span>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-4">
                                        <input
                                          type="text"
                                          value={rule.label}
                                          onChange={(e) => updateRule(rule.id, 'label', e.target.value)}
                                          className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                                        />
                                        <div className="flex items-center space-x-2">
                                          <span className="text-sm text-gray-600">Retenue:</span>
                                          <input 
                                            type="number" 
                                            value={rule.percentage}
                                            onChange={(e) => updateRule(rule.id, 'percentage', parseInt(e.target.value))}
                                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500"
                                            min="0"
                                            max="100"
                                          />
                                          <span className="text-sm text-gray-600">%</span>
                                        </div>
                                      </div>
                                      <div className="mt-1">
                                        <input
                                          type="text"
                                          value={rule.desc}
                                          onChange={(e) => updateRule(rule.id, 'desc', e.target.value)}
                                          className="text-xs text-gray-500 border border-gray-300 rounded px-2 py-1 w-full"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <select
                                        value={rule.color}
                                        onChange={(e) => updateRule(rule.id, 'color', e.target.value)}
                                        className="text-sm border border-gray-300 rounded px-2 py-1"
                                      >
                                        <option value="green">Vert</option>
                                        <option value="yellow">Jaune</option>
                                        <option value="orange">Orange</option>
                                        <option value="red">Rouge</option>
                                        <option value="blue">Bleu</option>
                                        <option value="purple">Violet</option>
                                      </select>
                                      <button
                                        onClick={() => saveRule(rule.id)}
                                        className="p-1 text-green-600 hover:text-green-800"
                                      >
                                        <Save size={16} />
                                      </button>
                                      <button
                                        onClick={() => deleteRule(rule.id)}
                                        className="p-1 text-red-600 hover:text-red-800"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  // Mode affichage
                                  <>
                                    <div className="w-20 text-sm font-medium text-gray-700">
                                      {generateRuleDisplayText(rule)}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-4">
                                        <span className="text-sm text-gray-600 w-32">{rule.label}</span>
                                        <div className="flex items-center space-x-2">
                                          <span className="text-sm text-gray-600">Retenue:</span>
                                          <input 
                                            type="number" 
                                            value={rule.percentage}
                                            onChange={(e) => updateRule(rule.id, 'percentage', parseInt(e.target.value))}
                                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500"
                                            min="0"
                                            max="100"
                                          />
                                          <span className="text-sm text-gray-600">%</span>
                                        </div>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">{rule.desc}</div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-3 h-3 rounded-full bg-${rule.color}-500`}></div>
                                      <button
                                        onClick={() => setEditingRuleId(rule.id)}
                                        className="p-1 text-gray-600 hover:text-gray-800"
                                      >
                                        <Edit size={14} />
                                      </button>
                                      <button
                                        onClick={() => deleteRule(rule.id)}
                                        className="p-1 text-red-600 hover:text-red-800"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Timeline visuel dynamique */}
                          <div className="mt-4">
                            <div className="text-sm font-medium text-gray-700 mb-2">Aperçu visuel de la politique</div>
                            <div className="flex h-8 rounded-lg overflow-hidden">
                              {cancellationRules.map((rule, index) => (
                                <div 
                                  key={rule.id}
                                  className={`bg-${rule.color}-500 flex-1 flex items-center justify-center text-white text-xs`}
                                >
                                  {100 - rule.percentage}%
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              {cancellationRules.map((rule) => (
                                <span key={rule.id}>{generateRuleDisplayText(rule)}</span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Conditions spéciales */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-4">Conditions spéciales</h5>
                          <div className="space-y-3">
                            <label className="flex items-center space-x-3">
                              <input type="checkbox" className="rounded" defaultChecked />
                              <span className="text-sm text-gray-700">Force majeure - remboursement complet</span>
                            </label>
                            <label className="flex items-center space-x-3">
                              <input type="checkbox" className="rounded" defaultChecked />
                              <span className="text-sm text-gray-700">Annulation de vol - 80% remboursé</span>
                            </label>
                            <label className="flex items-center space-x-3">
                              <input type="checkbox" className="rounded" defaultChecked />
                              <span className="text-sm text-gray-700">Raisons médicales justifiées - 90% remboursé</span>
                            </label>
                            <label className="flex items-center space-x-3">
                              <input type="checkbox" className="rounded" defaultChecked />
                              <span className="text-sm text-gray-700">Restrictions COVID-19 - remboursement complet</span>
                            </label>
                          </div>
                        </div>

                        {/* Calculateur de remboursement */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-800 mb-3">Calculateur de remboursement</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Montant total</label>
                              <div className="relative">
                                <input type="number" defaultValue="150000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" />
                                <span className="absolute right-3 top-2 text-sm text-gray-500">€</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Jours avant arrivée</label>
                              <input type="number" defaultValue="45" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500" />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Saison</label>
                              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 font-medium">
                                Ultra Peak Season
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-600 mb-1">€112,500</div>
                              <div className="text-sm text-blue-800">Montant remboursable (75%)</div>
                              <div className="text-xs text-blue-600 mt-1">Retenue: €37,500 • Règle: 30-60 jours</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Configuration Peak Season */}
                    {activePricingTab === 'peak-cancel' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Peak Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration Peak Season</div>
                          <p className="text-sm text-gray-400">Configurez les règles d'annulation pour la saison Peak (Nov - Avr)</p>
                        </div>
                      </div>
                    )}

                    {/* Configuration High Season */}
                    {activePricingTab === 'high-cancel' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration High Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration High Season</div>
                          <p className="text-sm text-gray-400">Configurez les règles d'annulation pour la saison High (Mai - Juin)</p>
                        </div>
                      </div>
                    )}

                    {/* Configuration Mid Season */}
                    {activePricingTab === 'mid-cancel' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-green-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Mid Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration Mid Season</div>
                          <p className="text-sm text-gray-400">Configurez les règles d'annulation pour la saison Mid (Jul - Août)</p>
                        </div>
                      </div>
                    )}

                    {/* Configuration Low Season */}
                    {activePricingTab === 'low-cancel' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                            <h4 className="text-lg font-semibold text-gray-800">Configuration Low Season</h4>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">
                            Valider les Modifications
                          </button>
                        </div>
                        <div className="text-center py-8">
                          <div className="text-gray-500 mb-2">Configuration Low Season</div>
                          <p className="text-sm text-gray-400">Configurez les règles d'annulation pour la saison Low (Sep - Oct)</p>
                        </div>
                      </div>
                    )}

                    {/* Default - pas de saison sélectionnée */}
                    {!['ultra-peak-cancel', 'peak-cancel', 'high-cancel', 'mid-cancel', 'low-cancel'].includes(activePricingTab) && (
                      <div className="text-center py-12">
                        <div className="text-gray-500 mb-4">Sélectionnez une saison</div>
                        <p className="text-sm text-gray-400">Cliquez sur une saison dans la sidebar pour configurer ses règles d'annulation</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary Panel (panneau de résumé) */}
                <div className="w-80 border-l border-gray-200 bg-gray-50">
                  <div className="p-4">
                    {/* Policy Preview */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Résumé de la Politique</h4>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="border-l-4 border-green-500 pl-3">
                            <div className="text-sm font-medium text-green-800">Remboursement complet</div>
                            <div className="text-xs text-green-600">90+ jours avant arrivée</div>
                          </div>
                          <div className="border-l-4 border-yellow-500 pl-3">
                            <div className="text-sm font-medium text-yellow-800">75% remboursable</div>
                            <div className="text-xs text-yellow-600">60-90 jours avant</div>
                          </div>
                          <div className="border-l-4 border-orange-500 pl-3">
                            <div className="text-sm font-medium text-orange-800">50% remboursable</div>
                            <div className="text-xs text-orange-600">30-60 jours avant</div>
                          </div>
                          <div className="border-l-4 border-red-500 pl-3">
                            <div className="text-sm font-medium text-red-800">Non remboursable</div>
                            <div className="text-xs text-red-600">Moins de 7 jours</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Simulation Tool */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Simulateur de Remboursement</h4>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Montant total</label>
                            <div className="relative">
                              <input type="number" defaultValue="150000" className="w-full px-3 py-2 text-sm border rounded-lg pr-8" />
                              <span className="absolute right-3 top-2 text-xs text-gray-500">€</span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Jours avant arrivée</label>
                            <input type="number" defaultValue="45" className="w-full px-3 py-2 text-sm border rounded-lg" />
                          </div>
                          <div className="p-3 bg-blue-50 rounded-lg text-center">
                            <div className="text-lg font-bold text-blue-600">€112,500</div>
                            <div className="text-xs text-blue-800">Montant remboursable (75%)</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Export Options */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Export & Partage</h4>
                      <div className="space-y-2">
                        <button className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                          📄 PDF Client
                        </button>
                        <button className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                          📊 Excel
                        </button>
                        <button className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                          📧 Email
                        </button>
                        <button className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                          🔗 Lien Public
                        </button>
                      </div>
                    </div>

                    {/* Conformité Légale */}
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-sm font-medium text-green-800">Conformité Légale</span>
                      </div>
                      <p className="text-xs text-green-700">Cette politique respecte les réglementations en vigueur pour les locations de courte durée.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="space-y-6">
            {/* Réservations */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Réservations actives</h3>
                <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                  Nouvelle Réservation
                </button>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    id: '#PRV2591',
                    client: 'Famille Johnson',
                    dates: '15-22 Oct 2024',
                    amount: '€280,000',
                    status: 'confirmed',
                    guests: 8
                  },
                  {
                    id: '#PRV2592',
                    client: 'Mr. & Mrs. Smith',
                    dates: '5-12 Nov 2024',
                    amount: '€315,000',
                    status: 'pending',
                    guests: 6
                  },
                  {
                    id: '#PRV2593',
                    client: 'Chen Family',
                    dates: '20-27 Dec 2024',
                    amount: '€450,000',
                    status: 'confirmed',
                    guests: 12
                  }
                ].map((booking) => (
                  <div key={booking.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center space-x-3">
                          <h4 className="font-medium text-gray-800">{booking.client}</h4>
                          <span className="text-sm text-gray-500">{booking.id}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{booking.dates} • {booking.guests} invités</p>
                        <p className="text-lg font-semibold text-gray-800 mt-2">{booking.amount}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        booking.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.status === 'confirmed' ? 'Confirmée' : 'En attente'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="space-y-6">
            {/* Galerie Photos */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Galerie Photos</h3>
                <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                  Ajouter Photos
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((i) => (
                  <div key={i} className="aspect-square bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                    <div className="text-center text-white">
                      <div className="text-2xl mb-1">📸</div>
                      <div className="text-xs">Photo {i}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'staff' && (
          <div className="space-y-6">
            {/* Staff & Services */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Personnel Affecté</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(villaData.staff || []).map((member, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">{member.name}</h4>
                        <p className="text-sm text-gray-600">{member.role}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">📞</span>
                        <span>{member.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">✉️</span>
                        <span className="text-blue-600">{member.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">🌐</span>
                        <span>{member.languages.join(', ')}</span>
                      </div>
                      {member.availability && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500">⏰</span>
                          <span>{member.availability}</span>
                        </div>
                      )}
                      {member.emergency && (
                        <span className="inline-flex px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Contact d'urgence
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Services programmés */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Services Programmés</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(villaData.services || {}).map(([service, schedule]) => (
                    <div key={service} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 capitalize mb-2">
                        {service.replace(/([A-Z])/g, ' $1')}
                      </h4>
                      <p className="text-sm text-gray-600">{schedule}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="space-y-6">
            {/* Maintenance */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Maintenance & Entretien</h3>
                <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
                  Programmer Maintenance
                </button>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    type: 'Maintenance Piscine',
                    date: '2024-10-15',
                    status: 'scheduled',
                    technician: 'Aqua Services',
                    description: 'Nettoyage complet et vérification équipements'
                  },
                  {
                    type: 'Climatisation',
                    date: '2024-10-20',
                    status: 'completed',
                    technician: 'Cool Tech SB',
                    description: 'Révision annuelle et changement filtres'
                  },
                  {
                    type: 'Jardin',
                    date: '2024-10-22',
                    status: 'in_progress',
                    technician: 'Garden Pro',
                    description: 'Taille des haies et entretien pelouse'
                  }
                ].map((maintenance, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-800">{maintenance.type}</h4>
                        <p className="text-sm text-gray-600 mt-1">{maintenance.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span>📅 {maintenance.date}</span>
                          <span>👷 {maintenance.technician}</span>
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        maintenance.status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : maintenance.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {maintenance.status === 'completed' ? 'Terminé' : 
                         maintenance.status === 'in_progress' ? 'En cours' : 'Programmé'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Analytics */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Analytics & Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Revenue Mensuel</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Septembre</span>
                      <span className="font-medium">€{(villaData.revenueYTD / 9 / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Août</span>
                      <span className="font-medium">€{(villaData.revenueYTD / 9 * 1.2 / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Juillet</span>
                      <span className="font-medium">€{(villaData.revenueYTD / 9 * 1.1 / 1000).toFixed(0)}K</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Taux d'Occupation</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">YTD</span>
                      <span className="font-medium">{villaData.occupancy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Objectif</span>
                      <span className="font-medium">85%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Tendance</span>
                      <span className="font-medium text-green-600">+5.2%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graphique simplifié */}
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-3">Évolution du Revenue (9 derniers mois)</h4>
                <div className="flex items-end space-x-2 h-32">
                  {[65, 70, 85, 90, 75, 80, 95, 88, 92].map((height, index) => (
                    <div key={index} className="flex-1 bg-yellow-500 rounded-t" style={{height: `${height}%`}}></div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Jan</span>
                  <span>Fév</span>
                  <span>Mar</span>
                  <span>Avr</span>
                  <span>Mai</span>
                  <span>Juin</span>
                  <span>Jul</span>
                  <span>Aoû</span>
                  <span>Sep</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VillaDetail;